// Types + pure helpers for the month-end personalization billing report
// (plan Task 2.4). The server does all the grouping and money math in integer
// cents (ghostfit-console/lib/personalization-billing.js); this file only
// mirrors its shape and formats for display, so nothing here can re-derive a
// charge and disagree with the invoice.

export type BillingRow = {
  submissionId: string;
  fundraiserName: string;
  orderId: string;
  orderNumber: string;
  orderDate: string;
  billingMonth: string;
  /** 'create-order' | 'webhook-safety-net' | 'none' */
  via: string;
  personalizedUnits: number;
  locationCount: number;
  rateCents: number | null;
  chargeCents: number;
  plainUnits: number;
  totalUnits: number | null;
  unitsSource: string;
  chargeMismatch: boolean;
  skuRepairNeeded: boolean;
  completedAt: string | null;
  shape?: string;
};

export type BillingTotals = {
  orderCount: number;
  personalizedUnits: number;
  locationCount: number;
  chargeCents: number;
  plainUnits: number;
};

export type BillingFundraiser = {
  submissionId: string;
  fundraiserName: string;
  orders: BillingRow[];
  totals: BillingTotals;
};

export type BillingReport = {
  month: string;
  generatedAt: string;
  fundraiserCount: number;
  orderCount: number;
  fundraisers: BillingFundraiser[];
  totals: BillingTotals;
  excludedOtherMonth: number;
  warnings: string[];
};

/** The 503 body the route returns when the collection-group index is missing. */
export type IndexRequired = { error: string; message: string; indexUrl: string | null; detail?: string };

const MONTH_RE = /^\d{4}-(0[1-9]|1[0-2])$/;

export function isValidMonth(month: string): boolean {
  return MONTH_RE.test(month);
}

/** 'YYYY-MM' for a Date, in LOCAL time (the picker is a human's calendar). */
export function monthKey(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

/**
 * The most recent `count` months, newest first, ending at `from`.
 * Built by walking a day-1 date backwards so a 31st never rolls a short month.
 */
export function recentMonths(count: number, from: Date = new Date()): string[] {
  const out: string[] = [];
  const cursor = new Date(from.getFullYear(), from.getMonth(), 1);
  for (let i = 0; i < count; i++) {
    out.push(monthKey(cursor));
    cursor.setMonth(cursor.getMonth() - 1);
  }
  return out;
}

/** '2026-08' → 'August 2026'. Returns the input unchanged when unparseable. */
export function monthLabel(month: string): string {
  if (!isValidMonth(month)) return month;
  const [y, m] = month.split('-').map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
}

/** Integer cents → '$42.00'. String math — never floating point. */
export function money(cents: number | null | undefined): string {
  if (!Number.isFinite(cents as number)) return '—';
  const n = Math.trunc(cents as number);
  const sign = n < 0 ? '-' : '';
  const abs = Math.abs(n);
  return `${sign}$${Math.floor(abs / 100).toLocaleString()}.${String(abs % 100).padStart(2, '0')}`;
}

/** Short, human label for where the personalization stamp came from. */
export function sourceLabel(via: string): string {
  if (via === 'webhook-safety-net') return 'safety net';
  if (via === 'create-order') return 'dashboard';
  return 'plain';
}

export function csvHref(month: string): string {
  return `/api/reports/personalization-billing?month=${encodeURIComponent(month)}&format=csv`;
}

export function reportHref(month: string): string {
  return `/api/reports/personalization-billing?month=${encodeURIComponent(month)}`;
}
